/* eslint-disable consistent-return */
import { check } from 'meteor/check';

import Atlas from '../Atlas';

import handleMethodException from '../../../modules/handle-method-exception';

const editAtlasSchema = Atlas.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'deleted',
  'shared',
  'sharedPrivate',
  'sharedUser',
  'publicId'
);
editAtlasSchema.extend({ _id: String });

/**
 * Inserts the atlas in the database.
 *
 * @async
 * @param {object} atlasObject - The atlas object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new atlas.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = async (atlasObject, userId) => {
  check(userId, String);
  try {
    Atlas.schema.validate(atlasObject, {
      check,
      keys: ['name', 'description', 'imageUrl', 'atlasType', 'atlasStructure']
    });
    return Atlas.insert({ owner: userId, ...atlasObject });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the atlas in the database.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} Return the id of the updated atlas.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = atlasObject => {
  try {
    editAtlasSchema.validate(atlasObject);
    const atlasId = atlasObject._id;
    Atlas.update(atlasId, { $set: atlasObject });
    return atlasId;
    // Return _id so we can redirect to atlas after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the atlas in the database.
 *
 * @param {string} atlasId - The atlas id.
 * @param {string} userId - The user id that wants the atlas.
 * @returns {string} Return the id of the new atlas copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const copy = (atlasId, userId) => {
  check(atlasId, String);
  check(userId, String);
  try {
    const copyAtlasData = Atlas.findOne({ _id: atlasId });
    delete copyAtlasData.owner;
    delete copyAtlasData._id;
    delete copyAtlasData.createdAt;
    delete copyAtlasData.updatedAt;
    delete copyAtlasData.shared;
    delete copyAtlasData.publicId;
    delete copyAtlasData.deleted;
    copyAtlasData.name += ' - copy';
    const copyAtlasId = Atlas.insert({ owner: userId, ...copyAtlasData });
    return copyAtlasId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the atlas in the database.
 *
 * @param {string} atlasId - The atlas id.
 * @returns {string} Return the id of the atlas.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = atlasId => {
  check(atlasId, String);
  try {
    return Atlas.update(atlasId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the atlas in the database.
 *
 * @param {string} atlasId - The atlas id.
 * @returns {string} Return the id of the atlas.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = atlasId => {
  check(atlasId, String);
  try {
    return Atlas.update(atlasId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the atlas from database.
 *
 * @param {string} atlasId - The atlas id.
 * @returns {string} Return the id of the deleted atlas.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = atlasId => {
  check(atlasId, String);
  try {
    return Atlas.remove(atlasId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the atlas shared or unshared.
 *
 * @param {string} atlasId - The atlas id.
 * @param {boolean} isShared - The new value for the shared key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleShared = (atlasId, isShared) => {
  check(atlasId, String);
  check(isShared, Boolean);
  try {
    Atlas.update(atlasId, { $set: { shared: isShared } });
  } catch (exception) {
    handleMethodException(exception);
  }
};
/**
 * Toggles the atlas shared or unshared.
 *
 * @param {string} atlasId - The atlas id.
 * @param {boolean} isShared - The new value for the shared key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleSharedPrivate = (atlasId, isShared) => {
  check(atlasId, String);
  check(isShared, Boolean);
  try {
    Atlas.update(atlasId, { $set: { sharedPrivate: isShared } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the atlas shared or unshared.
 *
 * @param {string} atlasId - The atlas id.
 * @param {boolean} isShared - The new value for the shared key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleSharedUser = (atlasId, isShared) => {
  check(atlasId, String);
  check(isShared, Boolean);
  try {
    Atlas.update(atlasId, { $set: { sharedUser: isShared } });
  } catch (exception) {
    handleMethodException(exception);
  }
};
